/*
 * Scentt compare \u2013 compare toggles and the compare bar (loaded on every page when compare is on).
 * The compare page itself is in scentt-compare-page.js, loaded only by the compare page section.
 *
 * Toggles: any [data-scentt-compare="<handle>"] button (product cards, product page). ONE delegated
 *   click listener keeps aria-pressed / .is-active / [data-scentt-label] in sync.
 * <scentt-compare-bar data-max data-page data-all data-need data-ready data-full data-hint \u2026>
 *   (snippets/scentt-compare-bar.liquid) \u2013 fixed bar with one slot per pick and "+" slots.
 * <scentt-compare-picker data-url data-section data-filters-js data-page data-count data-full> \u2013 pop-up opened by any
 *   [data-scentt-cmp-more] ("+" in the bar or on the compare page): the chosen collection's cards (section
 *   scentt-compare-picker, fetched with section rendering) with the Scentt filters, inside a modal <dialog>.
 * Storage: Scentt.list('scentt:compare') holds handles only; titles/images are cached (validated) in
 * scentt:compare-meta or re-read from the DOM.
 */
(() => {
  const start = () => {
    const S = window.Scentt && Object.isFrozen(window.Scentt) && typeof window.Scentt.html === 'function' ? window.Scentt : null;
    if (!S || customElements.get('scentt-compare-bar')) return;

    const LIMIT = 4;
    const picks = S.list('scentt:compare', LIMIT);
    const META_KEY = 'scentt:compare-meta';
    const H = (strings, ...values) => S.raw(S.html(strings, ...values));
    const safePath = (p, def) => (typeof p === 'string' && /^\/(?!\/)[^\s"'<>\\]*$/.test(p) ? p : def);
    const isImg = (u) => typeof u === 'string' && u.length < 800
      && (/^(https:)?\/\/[a-z0-9.-]+\/[^\s"'<>()\\]*$/i.test(u) || /^\/(?!\/)[^\s"'<>()\\]*$/.test(u));
    const preview = (sep) => {
      const t = window.Shopify && window.Shopify.theme;
      return t && t.role && t.role !== 'main' && S.isId(t.id) ? `${sep}preview_theme_id=${t.id}` : '';
    };
    const pretty = (h) => h.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const maxAllowed = () => {
      const src = document.querySelector('scentt-compare-bar') || document.querySelector('scentt-compare-page');
      return S.int(src && src.dataset.max, 3, 2, LIMIT);
    };

    /* ---------- title / image cache (untrusted: validated on every read) ---------- */
    const readMeta = () => {
      const raw = S.store.get(META_KEY, (v) => v && typeof v === 'object' && !Array.isArray(v), {});
      const out = {};
      Object.keys(raw).slice(0, 20).forEach((h) => {
        const m = raw[h];
        if (!S.isHandle(h) || !m || typeof m !== 'object') return;
        out[h] = { t: typeof m.t === 'string' ? m.t.slice(0, 150) : '', i: isImg(m.i) ? m.i : '' };
      });
      return out;
    };
    const saveMeta = (h, title, image) => {
      const meta = readMeta();
      const keep = {};
      picks.all().forEach((x) => { if (meta[x]) keep[x] = meta[x]; });
      keep[h] = { t: String(title || '').slice(0, 150), i: isImg(image) ? image : '' };
      S.store.set(META_KEY, keep);
    };
    const metaFor = (h) => {
      const btn = document.querySelector(`[data-scentt-compare="${h}"][data-title]`);
      const m = readMeta()[h] || { t: '', i: '' };
      const image = btn && isImg(btn.getAttribute('data-image')) ? btn.getAttribute('data-image') : m.i;
      return { t: (btn && btn.getAttribute('data-title')) || m.t || pretty(h), i: image };
    };

    /* ---------- toggles ---------- */
    const syncButtons = () => {
      const items = picks.all();
      document.querySelectorAll('[data-scentt-compare]').forEach((b) => {
        const on = items.includes(b.getAttribute('data-scentt-compare'));
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = b.querySelector('[data-scentt-label]');
        if (label) {
          const text = on ? label.getAttribute('data-on') : label.getAttribute('data-off');
          if (text) label.textContent = text;
        }
      });
    };
    const bar = () => document.querySelector('scentt-compare-bar');
    const picker = () => document.querySelector('scentt-compare-picker');
    const toggle = (btn) => {
      const h = btn.getAttribute('data-scentt-compare');
      if (!S.isHandle(h)) return 'invalid';
      if (picks.has(h)) { picks.remove(h); return 'removed'; }
      if (picks.all().length >= maxAllowed()) {
        const b = bar();
        if (b && b.flash) b.flash('full');
        const p = picker();
        if (p && p.flash) p.flash('full');
        syncButtons();
        return 'full';
      }
      saveMeta(h, btn.getAttribute('data-title'), btn.getAttribute('data-image'));
      picks.add(h, { front: false });
      const b = bar();
      if (b && b.flash) b.flash('added');
      return 'added';
    };
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-scentt-compare]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      toggle(btn);
    }, true);
    picks.onChange(syncButtons);
    /* Empty "+" slots (bar and compare page) open the pop-up when it is switched on; otherwise their link is followed. */
    document.addEventListener('click', (e) => {
      const more = e.target.closest && e.target.closest('[data-scentt-cmp-more]');
      if (!more) return;
      const p = picker();
      if (p && typeof p.open === 'function' && p.open(more)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    let queued = false;
    const queueSync = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; syncButtons(); });
    };
    new MutationObserver(queueSync).observe(document.body || document.documentElement, { childList: true, subtree: true });

    /* ---------- the bar ---------- */
    class ScenttCompareBar extends HTMLElement {
      connectedCallback() {
        this.slots = this.querySelector('[data-scentt-cmp-slots]');
        this.msg = this.querySelector('[data-scentt-cmp-msg]');
        this.go = this.querySelector('[data-scentt-cmp-go]');
        this.count = this.querySelector('[data-scentt-cmp-count]');
        this.max = S.int(this.dataset.max, 3, 2, LIMIT);
        this.page = safePath(this.dataset.page, '/pages/compare?view=compare');
        this.all = safePath(this.dataset.all, S.config.allUrl);
        if (this.go) this.go.setAttribute('href', this.page);
        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
        this.off = picks.onChange(() => this.render());
        this.render();
        syncButtons();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        if (this.off) this.off();
        clearTimeout(this.timer);
        document.body.classList.remove('scentt-cmp-on');
      }

      icon(name) {
        const tpl = this.querySelector('template[data-scentt-cmp-icons]');
        const el = tpl && tpl.content.querySelector(`[data-icon="${name}"]`);
        return el ? el.cloneNode(true) : document.createTextNode(name === 'plus' ? '+' : '\u00d7');
      }

      text(key, n) {
        return String(this.dataset[key] || '').replace('[n]', () => String(n));
      }

      render() {
        const items = picks.all().slice(0, this.max);
        const onPage = Boolean(document.querySelector('scentt-compare-page'));
        if (!items.length || onPage) {
          this.hidden = true;
          document.body.classList.remove('scentt-cmp-on');
          document.documentElement.style.removeProperty('--scentt-cmp-h');
          return;
        }
        const nodes = [];
        for (let k = 0; k < this.max; k += 1) {
          const h = items[k];
          const li = document.createElement('li');
          li.className = 'scentt-cmp-bar__slot';
          if (h) {
            const meta = metaFor(h);
            const img = document.createElement('span');
            img.className = 'scentt-cmp-bar__img';
            if (meta.i) {
              const el = document.createElement('img');
              el.src = meta.i;
              el.alt = '';
              el.width = 46;
              el.height = 46;
              el.loading = 'lazy';
              img.append(el);
            }
            const name = document.createElement('span');
            name.className = 'scentt-cmp-bar__name';
            name.textContent = meta.t;
            const x = document.createElement('button');
            x.type = 'button';
            x.className = 'scentt-cmp-bar__x';
            x.setAttribute('data-scentt-cmp-remove', h);
            x.setAttribute('aria-label', this.text('remove').replace('[title]', () => meta.t));
            x.append(this.icon('close'));
            li.append(img, name, x);
          } else {
            li.classList.add('scentt-cmp-bar__slot--empty');
            const a = document.createElement('a');
            a.className = 'scentt-cmp-bar__add';
            a.href = this.all;
            a.setAttribute('data-scentt-cmp-more', '');
            a.setAttribute('aria-label', this.dataset.addSlot || '');
            const img = document.createElement('span');
            img.className = 'scentt-cmp-bar__img';
            img.append(this.icon('plus'));
            a.append(img);
            li.append(a);
          }
          nodes.push(li);
        }
        this.slots.replaceChildren(...nodes);
        const ready = items.length >= 2;
        this.setMessage(ready ? this.text('ready', items.length) : this.text('need'), false);
        if (this.count) this.count.textContent = `(${items.length})`;
        if (this.go) {
          this.go.classList.toggle('is-disabled', !ready);
          if (ready) { this.go.removeAttribute('aria-disabled'); this.go.removeAttribute('tabindex'); } else {
            this.go.setAttribute('aria-disabled', 'true');
            this.go.setAttribute('tabindex', '-1');
          }
        }
        this.hidden = false;
        document.body.classList.add('scentt-cmp-on');
        requestAnimationFrame(() => document.documentElement.style.setProperty('--scentt-cmp-h', `${this.offsetHeight}px`));
      }

      setMessage(text, alert) {
        if (!this.msg) return;
        this.msg.textContent = text;
        this.msg.classList.toggle('is-alert', Boolean(alert));
        clearTimeout(this.timer);
        if (alert) this.timer = setTimeout(() => this.msg.classList.remove('is-alert'), 2600);
      }

      flash(kind) {
        const n = picks.all().length;
        if (kind === 'full') this.setMessage(this.text('full', this.max), true);
        else if (kind === 'hint') this.setMessage(this.text('hint'), true);
        else this.setMessage(n >= 2 ? this.text('ready', n) : this.text('need'), kind === 'need');
        if (S.reducedMotion || this.hidden) return;
        this.classList.remove('is-pulse');
        void this.offsetWidth; /* restart the animation */
        this.classList.add('is-pulse');
      }

      onClick(e) {
        const rm = e.target.closest('[data-scentt-cmp-remove]');
        if (rm) { picks.remove(rm.getAttribute('data-scentt-cmp-remove')); return; }
        if (e.target.closest('[data-scentt-cmp-clear]')) { picks.clear(); return; }
        const more = e.target.closest('[data-scentt-cmp-more]');
        if (more) {
          /* On a page that lists fragrances: stay, scroll to them and explain; elsewhere follow the link. */
          const grid = document.querySelector('#product-grid, #ProductGridContainer, [data-scentt-grid], .scentt-rails__list');
          if (grid) {
            e.preventDefault();
            grid.scrollIntoView({ behavior: S.reducedMotion ? 'auto' : 'smooth', block: 'start' });
            this.flash('hint');
          }
          return;
        }
        if (e.target.closest('[data-scentt-cmp-go].is-disabled')) {
          e.preventDefault();
          this.flash('need');
        }
      }
    }
    customElements.define('scentt-compare-bar', ScenttCompareBar);

    /* ---------- the "+" pop-up ---------- */
    /* The theme's own asset URL (same site or Shopify's CDN): //host/path.js?v=1, https://host/path.js or /path.js */
    const isScript = (u) => typeof u === 'string' && u.length < 600
      && /^(?:(?:https:)?\/\/[a-z0-9.-]+)?\/(?!\/)[^\s"'<>()\\]*\.js(?:\?[^\s"'<>()\\]*)?$/i.test(u);
    /** Loads assets/scentt-filters.js once (only collection and search pages load it themselves). */
    const loadFilters = (src, onFail) => {
      if (customElements.get('scentt-filters') || document.querySelector('script[data-scentt-filters-js]')) return;
      let host = '';
      try { host = isScript(src) ? new URL(src, window.location.href).hostname : ''; } catch (e) { host = ''; }
      if (!host || !(host === window.location.hostname || /(^|\.)(shopify\.com|shopifycdn\.com)$/.test(host))) { onFail(); return; }
      const tag = document.createElement('script');
      tag.src = src;
      tag.defer = true;
      tag.setAttribute('data-scentt-filters-js', '');
      tag.addEventListener('error', onFail, { once: true });
      document.head.append(tag);
    };

    class ScenttComparePicker extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        /* Browsers without <dialog>: the "+" slots keep their link to all products */
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
        this.url = safePath(this.dataset.url, '');
        this.content = this.querySelector('[data-picker-content]');
        this.status = this.querySelector('[data-picker-status]');
        this.error = this.querySelector('[data-picker-error]');
        this.count = this.querySelector('[data-picker-count]');
        this.go = this.querySelector('[data-picker-go]');
        this.goCount = this.querySelector('[data-picker-go-count]');
        if (this.go) this.go.setAttribute('href', safePath(this.dataset.page, '/pages/compare?view=compare'));
        this.onClick = this.onClick.bind(this);
        this.onKey = this.onKey.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onClose = this.onClose.bind(this);
        this.dialog.addEventListener('click', this.onClick);
        this.dialog.addEventListener('keydown', this.onKey, true);
        this.dialog.addEventListener('cancel', this.onCancel);
        this.dialog.addEventListener('close', this.onClose);
        this.off = picks.onChange(() => this.update());
        this.ready = true;
      }

      disconnectedCallback() {
        if (!this.ready) return;
        this.dialog.removeEventListener('click', this.onClick);
        this.dialog.removeEventListener('keydown', this.onKey, true);
        this.dialog.removeEventListener('cancel', this.onCancel);
        this.dialog.removeEventListener('close', this.onClose);
        if (this.off) this.off();
        clearTimeout(this.timer);
        document.documentElement.classList.remove('scentt-picker-lock');
        this.ready = false;
      }

      /** Opens the pop-up; false when it can't (no <dialog> support or no collection), so the "+" link is followed. */
      open(opener) {
        if (!this.ready || !this.url) return false;
        this.opener = opener instanceof HTMLElement ? opener : null;
        if (!this.dialog.open) {
          this.dialog.showModal();
          document.documentElement.classList.add('scentt-picker-lock');
        }
        this.update();
        if (!this.loaded && !this.loading) this.load();
        return true;
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }

      async load() {
        this.loading = true;
        this.status.hidden = false;
        this.error.hidden = true;
        try {
          const sep = this.url.includes('?') ? '&' : '?';
          const doc = await S.fetchSection(`${this.url}${preview(sep)}`, this.dataset.section || 'scentt-compare-picker');
          const root = doc.querySelector('[data-scentt-picker-root]');
          if (!root) throw new S.Error('picker markup missing');
          /* Scripts in fetched markup never run (scentt-filters.js is loaded from the theme's own asset URL instead), and
             <noscript> is dropped: the parser reads it with scripting off, so its no-JS styles would apply here. */
          root.querySelectorAll('script, noscript').forEach((n) => n.remove());
          this.content.replaceChildren(document.importNode(root, true));
          this.loaded = true;
          loadFilters(this.dataset.filtersJs, () => {
            /* Without the filters script every card is still shown, just without filters */
            const grid = this.content.querySelector('#ScenttPickerGrid');
            if (grid) grid.classList.add('is-scentt-ready');
            this.content.querySelectorAll('scentt-filters').forEach((f) => { f.hidden = true; });
          });
          syncButtons();
        } catch (e) {
          S.warn('compare picker', e);
          this.error.hidden = false;
        } finally {
          this.loading = false;
          this.status.hidden = true;
        }
      }

      text(key, map) {
        return String(this.dataset[key] || '').replace(/\[(\w+)\]/g, (all, k) => (k in map ? String(map[k]) : all));
      }

      update() {
        const n = picks.all().length;
        const max = maxAllowed();
        if (this.count && !this.count.classList.contains('is-alert')) this.count.textContent = this.text('count', { n, max });
        if (this.goCount) this.goCount.textContent = `(${n})`;
        if (this.go) {
          const ready = n >= 2;
          this.go.classList.toggle('is-disabled', !ready);
          if (ready) { this.go.removeAttribute('aria-disabled'); this.go.removeAttribute('tabindex'); } else {
            this.go.setAttribute('aria-disabled', 'true');
            this.go.setAttribute('tabindex', '-1');
          }
        }
      }

      flash(kind) {
        if (kind !== 'full' || !this.count || !this.dialog.open) return;
        this.count.textContent = this.text('full', { n: maxAllowed() });
        this.count.classList.add('is-alert');
        clearTimeout(this.timer);
        this.timer = setTimeout(() => { this.count.classList.remove('is-alert'); this.update(); }, 2600);
      }

      onClick(e) {
        /* A click on the dialog itself (not its contents) is a click on the dimmed backdrop */
        if (e.target === this.dialog || e.target.closest('[data-picker-close]')) { this.close(); return; }
        if (e.target.closest('[data-picker-retry]')) { this.load(); return; }
        if (e.target.closest('[data-picker-go].is-disabled')) e.preventDefault();
      }

      /* Esc closes an open filters panel first, then the pop-up */
      onKey(e) {
        if (e.key === 'Escape') this.panelOpen = Boolean(this.querySelector('scentt-filters.is-open'));
      }

      onCancel(e) {
        if (this.panelOpen) e.preventDefault();
        this.panelOpen = false;
      }

      onClose() {
        const filters = this.querySelector('scentt-filters.is-open');
        if (filters && typeof filters.toggle === 'function') filters.toggle(false);
        document.documentElement.classList.remove('scentt-picker-lock');
        /* Back to the "+" that opened it; if that has been redrawn since: the bar's "+", the bar's Compare button,
           or another "+" on the page (the compare page, where the bar is hidden) */
        const visible = (el) => el && el.isConnected && el.getClientRects().length > 0;
        const target = [this.opener, document.querySelector('scentt-compare-bar [data-scentt-cmp-more]'),
          document.querySelector('scentt-compare-bar [data-scentt-cmp-go]'), document.querySelector('[data-scentt-cmp-more]')].find(visible);
        if (target) target.focus({ preventScroll: true });
      }
    }
    customElements.define('scentt-compare-picker', ScenttComparePicker);
  };

  if (window.Scentt) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
